import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckCircle2Icon, CreditCardIcon, DownloadIcon, LockIcon, ZapIcon } from 'lucide-react';
import { CTAButton } from './Shared';
import { BodySilhouette, DnaHelix, LeafSprig, Mandala, TreeOfLife } from './BioDecor';
const productImage = 'https://cdn.magicpatterns.com/uploads/5VFCV6oCSudmsxYWXNQ9nq/ChatGPT_Image_4_sept_2026,_14_25_51.png';
const stats = [{
  icon: DownloadIcon,
  text: 'Listo para imprimir o usar en tablet'
}, {
  icon: LockIcon,
  text: 'Pago 100% seguro con Mercado Pago'
}, {
  icon: ZapIcon,
  text: 'Acceso en 5 minutos'
}];
export function Hero() {
  return <header className="relative overflow-hidden bg-brand-blueDeep pb-8 pt-5 text-brand-cream md:pb-20 md:pt-16" data-id="element-123" data-mp="s9gj46ak8sbc">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-124" data-mp="nekmesudpgrw">
        <TreeOfLife className="animate-float-slow absolute -left-8 top-10 h-72 w-44 text-brand-greenSoft/30 md:h-96 md:w-56" data-id="element-125" data-mp="3cz5u28857tyr" />
        <LeafSprig className="animate-float-soft absolute -right-8 top-24 h-72 w-44 -scale-x-100 text-brand-blueSoft/25 md:h-[26rem] md:w-56" data-id="element-126" data-mp="3j8sg632wlnqw" />
        <DnaHelix className="animate-float-soft absolute left-[8%] top-[48%] hidden h-56 w-24 text-brand-goldLight/25 lg:block" data-id="element-127" data-mp="3o73ouahgps7j" />
        <BodySilhouette className="animate-float-slow absolute bottom-8 right-[7%] hidden h-56 w-28 text-brand-blueSoft/25 lg:block" data-id="element-128" data-mp="39cpawocr98zq" />
        <Mandala className="absolute right-[16%] top-[16%] h-14 w-14 text-brand-amber/35 md:h-20 md:w-20" data-id="element-129" data-mp="30hrgfctylo6a" />
        <div className="absolute -top-24 left-1/2 h-72 w-[36rem] -translate-x-1/2 rounded-full bg-brand-blueMid/40 blur-[120px]" data-id="element-130" data-mp="359t19n570n7x" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8" data-id="element-131" data-mp="2qhqv5lcj4xzg">
        <motion.div initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-132" data-mp="2vj7tb93mq6cc">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-brand-gold/50 bg-white/10 px-3 py-1 backdrop-blur-sm md:mb-4 md:px-4 md:py-1.5" data-id="element-133" data-mp="2ljgyf6b5ppbq">
            <span className="text-xs md:text-sm" data-id="element-134" data-mp="nat65811aej4">🔮</span>
            <span className="font-title text-[10px] font-bold tracking-[.18em] text-brand-goldLight md:text-xs" data-id="element-135" data-mp="n98cdkmjmzmb">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="mx-auto max-w-5xl" data-id="element-136" data-mp="deq74b1haw0e">
            <h1 className="neon-title font-serif text-[26px] font-bold leading-[1.1] tracking-tight md:text-[clamp(2.4rem,6.4vw,4.8rem)] md:leading-[1.02] md:tracking-[-0.035em]" data-id="element-137" data-mp="1b0w05toidukd">
              <span className="block" data-id="element-138" data-mp="15zzubakljycb">Sesiones más seguras,</span>
              <span className="neon-accent block" data-id="element-139" data-mp="111eqz2a1syjn">más profundas</span>
              <span className="block" data-id="element-140" data-mp="x1nxurx1u6by">y sin improvisar.</span>
            </h1>
            <div className="neon-underline mx-auto mt-3.5 w-32 md:mt-5 md:w-64" aria-hidden="true" data-id="element-141" data-mp="3lsyth1deqqk4" />
            <p className="mx-auto mt-3.5 max-w-2xl text-[13px] font-medium leading-relaxed text-brand-cream/80 md:mt-5 md:text-lg" data-id="element-142" data-mp="3hrdbx2pxho3l">
              El conocimiento ya lo tenés. El sistema para aplicarlo con autoridad profesional y generar resultados
              tangibles en tus pacientes, ahora también.
            </p>
          </div>

          <div className="mx-auto mt-4 flex max-w-2xl justify-center md:mt-10" data-id="element-143" data-mp="268y4ph5tbx2z">
            <motion.img src={productImage} alt="Kit Maestro de Biodescodificación: el manual principal con sus siete láminas de protocolos, fichas y recursos" initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} className="h-auto w-[82%] max-w-[300px] drop-shadow-2xl sm:w-[80%] sm:max-w-[380px] md:w-[94%] md:max-w-[600px]" draggable={false} fetchPriority="high" data-id="element-144" data-mp="2b192oegzt8sw" />
          </div>

          <div className="mx-auto mt-4 max-w-3xl space-y-2.5 md:mt-6 md:space-y-3" data-id="element-145" data-mp="zlaqw2ufk8nz">
            <h2 className="font-title text-[15px] font-bold leading-snug tracking-normal text-brand-goldLight md:text-3xl md:tracking-wide" data-id="element-146" data-mp="zndop91tr639">
              🧬 KIT MAESTRO DE BIODESCODIFICACIÓN
            </h2>
            <div className="leaf-rule mx-auto max-w-xs" aria-hidden="true" data-id="element-147" data-mp="e9k1g57dztx6" />
            <p className="mx-auto max-w-xl font-serif text-[13px] font-medium leading-snug text-brand-cream/90 md:text-xl md:leading-normal" data-id="element-148" data-mp="e7h8bcaxtko8">
              Sistema profesional de protocolos, rituales y herramientas de biodescodificación, listo para aplicar
              desde tu próxima consulta.
            </p>
            <CTAButton variant="gold" className="cta-shimmer relative mt-2 overflow-hidden px-6 py-3 text-sm md:mt-3 md:px-10 md:py-5 md:text-xl" data-id="element-149" data-mp="3rg3jfm70wo3n">
              <span className="relative z-10" data-id="element-150" data-mp="3mnitwwv5gx5x">→ VER EL KIT</span>
            </CTAButton>
          </div>

          <div className="mx-auto mt-6 grid max-w-3xl gap-3 rounded-2xl border border-white/15 bg-white/5 p-4 backdrop-blur-sm sm:grid-cols-3 md:mt-10 md:p-5" data-id="element-151" data-mp="2yxg4ypmj87bj">
            {stats.map(stat => {
            const Icon = stat.icon;
            return <div key={stat.text} className="flex flex-col items-center gap-2 text-center" data-id="element-152" data-mp="1ea5l4u310zmp">
                  <Icon className="h-6 w-6 text-brand-amber" data-id="element-153" data-mp="zlsl9m0tiwmx" />
                  <span className="text-xs leading-snug text-brand-cream/80 md:text-sm" data-id="element-154" data-mp="14javdfqknhwi">{stat.text}</span>
                </div>;
          })}
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2 text-[10px] font-semibold md:mt-6 md:text-sm" data-id="element-155" data-mp="1s7c16gwd8k88">
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-brand-cream/85" data-id="element-156" data-mp="1j8guz4tbna4x">
              <BookOpenIcon className="mr-1.5 inline h-3.5 w-3.5 text-brand-amber" data-id="element-157" data-mp="1n9sfivxc9569" />
              100% PDF
            </span>
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-brand-cream/85" data-id="element-158" data-mp="7s8mkmr7l4lc">
              <CheckCircle2Icon className="mr-1.5 inline h-3.5 w-3.5 text-brand-amber" data-id="element-159" data-mp="bvompt2hln2s" />
              Garantía 7 días
            </span>
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-brand-cream/85" data-id="element-160" data-mp="2ejeq1vk58z1y">
              <CreditCardIcon className="mr-1.5 inline h-3.5 w-3.5 text-brand-amber" data-id="element-161" data-mp="24rr2i5qpk25r" />
              Mercado Pago
            </span>
            <span className="rounded-full border border-brand-gold/50 bg-brand-amber/15 px-3 py-1.5 text-brand-goldLight" data-id="element-162" data-mp="1zrmnkk5g7fhn">
              🌿 El kit argentino más completo
            </span>
          </div>
        </motion.div>
      </div>
    </header>;
}